
import React from 'react';
import PageLayout from '@/components/layout/PageLayout';
import PageHero from '@/components/ui/PageHero';
import ScrollReveal from '@/components/ui/ScrollReveal';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Mic, Lightbulb, Brain, Bot, Workflow, Video, Router, CreditCard, Smartphone, Users, BookOpen, Award } from 'lucide-react';
import FAQSection from '@/components/ui/FAQSection';
import PricingCard from '@/components/ui/PricingCard'; // Added missing import

const TechCoaching = () => {
  const pricingCards = [
    {
      title: "1-on-1 Coaching Session",
      price: "$250",
      description: "A comprehensive 1:30hr coaching session focused on your specific technology and strategy needs.",
      features: [
        { text: "Personalized coaching", included: true },
        { text: "Technology assessment", included: true },
        { text: "Strategic mindmapping", included: true },
        { text: "Vision structuring", included: true },
        { text: "AI implementation consultation", included: true },
        { text: "Technology identification", included: true },
      ],
      popular: true,
      ctaText: "Book Now",
      ctaLink: "/contact",
      hideMonthly: true
    },
    {
      title: "Custom Solutions",
      price: "Custom",
      description: "Need something different? We offer customized coaching packages tailored to your specific needs.",
      features: [
        { text: "Multiple sessions", included: true },
        { text: "Ongoing support", included: true },
        { text: "Custom implementation help", included: true },
        { text: "Business strategy alignment", included: true },
        { text: "Specialized AI training", included: true },
        { text: "Tech workflow optimization", included: true },
      ],
      popular: false,
      ctaText: "Contact Us",
      ctaLink: "/contact",
      hideMonthly: true
    }
  ];

  const faqs = [
    {
      question: "How long does it typically take to set up a professional podcast?",
      answer: "The timeline varies based on your specific needs and current setup, but generally, we can help you launch a professional podcast within 2-4 weeks. This includes equipment selection, recording space optimization, show format development, and distribution setup on major platforms."
    },
    {
      question: "Do I need technical experience to benefit from your coaching?",
      answer: "Not at all! Our coaching is designed for all technical skill levels, from complete beginners to those with some experience. We simplify complex technology concepts and provide step-by-step guidance tailored to your learning pace and needs."
    },
    {
      question: "Can you help with integrating AI into my existing business processes?",
      answer: "Absolutely. We specialize in identifying opportunities to integrate AI tools like ChatGPT, Midjourney, and custom AI solutions into your existing workflows. We'll help you implement AI for content creation, customer service, data analysis, and more while providing the training needed for your team to use these tools effectively."
    },
    {
      question: "What types of automation can you help implement for my business?",
      answer: "We can implement various automation solutions including email marketing sequences, customer onboarding flows, appointment scheduling, payment processing, social media posting, lead qualification, and custom workflow automation using tools like Zapier, Make (Integromat), Pabbly Connect, IFTTT, and more."
    },
    {
      question: "Do you offer ongoing support after the initial coaching period?",
      answer: "Yes, we offer flexible ongoing support options to ensure your continued success. This can include regular check-in sessions, technical troubleshooting, strategy refinement, and guidance on new technology implementations as your business evolves and grows."
    }
  ];

  return (
    <PageLayout 
      title="Tech Coaching | Astrem" 
      description="Personalized coaching for podcast creation, AI implementation, automation, and digital transformation for businesses."
      ogImage="public/lovable-uploads/0c97a82f-94b5-4ccd-879b-fcd304f2d0ab.png"
    >
      <PageHero
        title="Tech Coaching"
        subtitle="Expert Guidance for Your Digital Journey"
        description="Navigate the complex world of technology with confidence. Our personalized coaching helps you implement the right tech solutions to grow your business or personal brand."
        badge="Coaching Services"
        backgroundImage="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Person_writing_in_notebook_outdoors.jpg"
        ctaText="Schedule a Consultation"
        ctaLink="/contact"
      />

      {/* Main Services Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
              Our Coaching Services
            </span>
          </ScrollReveal>
          
          <ScrollReveal delay={100}>
            <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
              Personalized Tech Guidance for Your Success
            </h2>
          </ScrollReveal>
          
          <ScrollReveal delay={200}>
            <p className="text-lg text-astrem-gray-600 max-w-3xl mb-12">
              We provide expert coaching to help you navigate technology, launch successful digital initiatives, and transform your business or personal brand.
            </p>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <ScrollReveal delay={300}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Mic className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">Podcast Coaching</h3>
                <p className="text-astrem-gray-600">
                  Expert guidance on podcast setup, equipment selection, recording techniques, distribution, and content strategy to launch your successful podcast.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Lightbulb className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">Digital Transformation</h3>
                <p className="text-astrem-gray-600">
                  Guidance on transitioning your business from analog to digital, implementing online systems, and creating an effective digital presence.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={500}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Brain className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">Marketing Mindset</h3>
                <p className="text-astrem-gray-600">
                  Develop the right approach to digital marketing, overcoming mental blocks, and building confidence in your marketing strategy.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={600}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Bot className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">AI Implementation</h3>
                <p className="text-astrem-gray-600">
                  Learn effective AI prompting techniques and how to integrate AI tools into your workflow to boost productivity and creativity.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={700}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Workflow className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">Automation Coaching</h3>
                <p className="text-astrem-gray-600">
                  Guidance on implementing automation tools and platforms to streamline your business processes and save valuable time.
                </p>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={800}>
              <div className="p-6 border border-astrem-gray-200 rounded-lg hover:shadow-md transition-shadow">
                <div className="w-12 h-12 bg-astrem-gray-100 rounded-full flex items-center justify-center mb-4">
                  <Users className="h-6 w-6 text-astrem-black" />
                </div>
                <h3 className="text-xl font-medium mb-3">CRM Setup & Training</h3>
                <p className="text-astrem-gray-600">
                  Expert assistance in selecting, setting up, and maximizing the potential of CRM platforms to manage your customer relationships effectively.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Additional Services Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                More Solutions
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Comprehensive Tech Support
              </h2>
              <p className="text-lg text-astrem-gray-600">
                Beyond our core coaching services, we offer specialized guidance in several key areas to ensure your complete digital success.
              </p>
            </div>
          </ScrollReveal>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <ScrollReveal delay={200}>
              <div className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="flex-shrink-0 mr-4">
                  <Video className="h-10 w-10 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-medium mb-2">Video Content Creation</h3>
                  <p className="text-astrem-gray-600">
                    Expert advice on creating engaging video content for social media, including recording techniques, equipment selection, and post-production strategies. We can also arrange professional editing with captions and music.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={300}>
              <div className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="flex-shrink-0 mr-4">
                  <Router className="h-10 w-10 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-medium mb-2">WiFi & Lead Generation</h3>
                  <p className="text-astrem-gray-600">
                    Guidance on setting up captive portal WiFi systems for restaurants and retail businesses to capture customer information and integrate with your CRM for effective lead management.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={400}>
              <div className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="flex-shrink-0 mr-4">
                  <CreditCard className="h-10 w-10 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-medium mb-2">Payment Platform Setup</h3>
                  <p className="text-astrem-gray-600">
                    Assistance in selecting and implementing the right payment platforms for your business, including online booking systems, subscription models, and e-commerce solutions.
                  </p>
                </div>
              </div>
            </ScrollReveal>

            <ScrollReveal delay={500}>
              <div className="bg-white p-6 rounded-lg shadow-sm flex">
                <div className="flex-shrink-0 mr-4">
                  <Smartphone className="h-10 w-10 text-primary" />
                </div>
                <div>
                  <h3 className="text-xl font-medium mb-2">App Development Consulting</h3>
                  <p className="text-astrem-gray-600">
                    Strategic guidance for entrepreneurs and businesses looking to develop mobile or web applications, from concept refinement to technology selection and development approach.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <ScrollReveal>
              <div>
                <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                  Our Approach
                </span>
                <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                  How We Help You Succeed
                </h2>
                <p className="text-lg text-astrem-gray-600 mb-8">
                  Our coaching methodology is designed to empower you with the knowledge, skills, and confidence to leverage technology effectively.
                </p>
                
                <div className="space-y-6">
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center">
                        <span className="font-medium">1</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">Discovery & Assessment</h3>
                      <p className="text-astrem-gray-600">
                        We begin by understanding your goals, current technical setup, and specific challenges to create a tailored coaching plan.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center">
                        <span className="font-medium">2</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">Strategy Development</h3>
                      <p className="text-astrem-gray-600">
                        Together, we create a practical implementation strategy with clear steps, timelines, and outcomes.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center">
                        <span className="font-medium">3</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">Hands-On Guidance</h3>
                      <p className="text-astrem-gray-600">
                        Our coaching sessions provide practical, step-by-step instruction tailored to your learning style and pace.
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex">
                    <div className="flex-shrink-0 mr-4">
                      <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center">
                        <span className="font-medium">4</span>
                      </div>
                    </div>
                    <div>
                      <h3 className="text-lg font-medium mb-2">Implementation Support</h3>
                      <p className="text-astrem-gray-600">
                        We provide ongoing support as you implement new technologies, helping you overcome obstacles and optimize your results.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <img
                src="https://portal.astrem.co/users/u9/9466/astremagency/lovableagentsite/Person_working_with_laptop_notes.jpg"
                alt="Tech Coaching Approach"
                className="rounded-lg shadow-lg w-full h-auto object-cover"
              />
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* Team Ready Section */}
      <section className="py-20 bg-astrem-black text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="max-w-3xl mx-auto text-center">
              <span className="inline-block px-3 py-1 bg-white/10 text-white rounded-full text-sm font-medium mb-4 backdrop-blur-sm">
                Our Expertise
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Finding the Right Solutions for Your Needs
              </h2>
              <p className="text-lg text-astrem-gray-300 mb-8">
                With years of experience and a creative, solution-focused mindset, we're dedicated to helping you navigate the complex digital landscape. Our approach combines technical expertise with a strong vision for what's possible, providing you with the guidance needed to implement effective technology solutions.
              </p>
              
              <div className="flex flex-col md:flex-row justify-center items-center space-y-6 md:space-y-0 md:space-x-10 mb-10">
                <div className="flex items-center">
                  <BookOpen className="h-6 w-6 mr-3 text-primary" />
                  <span className="text-lg">Years of Industry Experience</span>
                </div>
                <div className="flex items-center">
                  <Lightbulb className="h-6 w-6 mr-3 text-primary" />
                  <span className="text-lg">Creative Problem-Solving</span>
                </div>
                <div className="flex items-center">
                  <Award className="h-6 w-6 mr-3 text-primary" />
                  <span className="text-lg">Vision-Driven Approach</span>
                </div>
              </div>
              
              <p className="text-lg text-astrem-gray-300 mb-8">
                We believe in finding customized solutions that address your specific challenges. Our coaching isn't about following a rigid formula—it's about understanding your unique situation and crafting a technology strategy that aligns with your goals. We're committed to empowering you with the knowledge, tools, and confidence needed to thrive in today's digital world.
              </p>
              
              <Button asChild size="lg" className="bg-white text-astrem-black hover:bg-astrem-gray-100 rounded-full px-8">
                <Link to="/contact">Schedule a Consultation</Link>
              </Button>
            </div>
          </ScrollReveal>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <ScrollReveal>
            <div className="text-center mb-12">
              <span className="inline-block px-3 py-1 bg-astrem-gray-100 text-astrem-gray-800 rounded-full text-sm font-medium mb-4">
                Coaching Packages
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Coaching Packages
              </h2>
              <p className="text-lg text-astrem-gray-600 max-w-3xl mx-auto">
                Flexible coaching options designed to meet your specific needs and goals.
              </p>
            </div>
          </ScrollReveal>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pricingCards.map((card, index) => (
              <ScrollReveal key={index} delay={200 + (index * 100)}>
                <PricingCard {...card} />
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FAQSection
        title="Frequently Asked Questions"
        description="Find answers to common questions about our tech coaching services."
        faqs={faqs}
      />

      {/* CTA Section */}
      <section className="py-20 bg-astrem-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <ScrollReveal>
              <h2 className="text-3xl md:text-4xl font-display font-medium mb-6">
                Ready to Navigate the Digital World with Confidence?
              </h2>
            </ScrollReveal>
            
            <ScrollReveal delay={100}>
              <p className="text-lg text-astrem-gray-600 mb-8">
                Contact us today to schedule a free initial consultation and discover how our tech coaching can help you achieve your digital goals.
              </p>
            </ScrollReveal>
            
            <ScrollReveal delay={200}>
              <Button asChild size="lg" className="bg-astrem-black hover:bg-astrem-gray-800 text-white rounded-full px-8">
                <Link to="/contact">Get Started Today</Link>
              </Button>
            </ScrollReveal>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default TechCoaching;
